import { useEffect, useLayoutEffect, useState } from 'react';
import { WAITING_MOTION } from '@featit/shared/brand/waiting-motion';
import { whenLaunchContentSettles } from '@/shell/screenSettled';
import { Waiting } from './Waiting';

/**
 * F-0578 WAIT-9 (`docs/concept-waiting-motion.md`): settle, then leave. Once the session is in,
 * the page is drawn under the launch wait, which stays over it on the launch canvas until the
 * page's content is in (`whenLaunchContentSettles`, LS-2): one loading state, never a skeleton
 * after it. Then, in that same frame, the whole overlay fades (`fadeMs`) while its study eases to
 * the rest shape, and is gone.
 *
 * Captain's decision on I-1718's measurement (a 0.3 s opaque hold over a page already in, whose
 * clicks reached a page nobody could see):
 * - no fixed hold: the fade starts the frame the content is in;
 * - until then it takes pointer events, so a click never lands on something unseen;
 * - during the fade clicks pass through to the page;
 * - under Reduce Motion the same fade, nothing moving: it is `data-motion="fade"`, the stylesheet's
 *   exemption from its 0.01 ms rule (IU-3b, T-0670).
 *
 * It is not announced: the page is. While it is up the root carries `LAUNCH_SETTLING_ATTRIBUTE`
 * (I-1718): the named end a reader waits for (the e2e fixture's screenshots), set before the first
 * paint and gone with the overlay.
 */
export const LAUNCH_SETTLING_ATTRIBUTE = 'data-launch-settling';

export function LaunchSettle({ onDone }: { onDone: () => void }) {
  // `performance.now()` when the content came in: from then the fade, and the study's settle.
  const [fadeFrom, setFadeFrom] = useState<number | null>(null);
  const { fadeMs } = WAITING_MOTION;

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.setAttribute(LAUNCH_SETTLING_ATTRIBUTE, '');
    return () => root.removeAttribute(LAUNCH_SETTLING_ATTRIBUTE);
  }, []);

  useEffect(() => whenLaunchContentSettles(() => setFadeFrom(performance.now())), []);

  useEffect(() => {
    if (fadeFrom === null) return undefined;
    const toDone = setTimeout(onDone, fadeMs);
    return () => clearTimeout(toDone);
  }, [fadeFrom, fadeMs, onDone]);

  const fading = fadeFrom !== null;
  return (
    <div
      data-launch-settle={fading ? 'fading' : 'holding'}
      data-motion="fade"
      aria-hidden="true"
      className={`${fading ? 'pointer-events-none' : 'pointer-events-auto'} fixed inset-0 z-[100] flex items-center justify-center bg-[var(--evx-canvas)] transition-opacity ease-out`}
      style={{ opacity: fading ? 0 : 1, transitionDuration: `${fadeMs}ms` }}
    >
      <Waiting size="launch" silent settleFrom={fadeFrom ?? undefined} />
    </div>
  );
}
